import { Button } from '@edsn/pie-components';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { CancelDataServicesRequest } from '#pie/components/cancel-data-services-request/CancelDataServicesRequest';
import { Card } from '#pie/components/card/Card';
import { Page, PageHeader } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { Stepper } from '#pie/components/stepper/Stepper';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  type CreateDataRequestDto,
  usePostDataRequest,
} from '#pie/internal/api/pie-bff';
import { DataServicesFormStep1 } from './step-1/DataServicesFormStep1';
import { DataServicesFormStep2 } from './step-2/DataServicesFormStep2';

export const DataServicesForm = () => {
  const { t } = useTranslation();
  const { t: tdr } = useTranslation('dataRequest');
  const navigate = useNavigate();
  const { addToast } = useToast();

  const step1 = 0;
  const step2 = 1;

  const [step, setStep] = useState(step1);
  const [shouldBlock, setShouldBlock] = useState(true);
  const [noLegalBasis, setNoLegalBasis] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formHelpers = useForm<CreateDataRequestDto>();
  const { handleSubmit } = formHelpers;

  const { mutateAsync, isSuccess } = usePostDataRequest({
    mutation: {
      onError: (error) => {
        addToast({
          message: tdr('toast.error.message'),
          title: tdr('toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        setShouldBlock(false);
        addToast({
          message: tdr('toast.success.message'),
          title: tdr('toast.success.title'),
          type: 'success',
        });
      },
    },
  });

  useEffect(() => {
    if (isSuccess) navigate('/datadiensten');
  }, [isSuccess, navigate]);

  useEffect(() => {}, [step]);

  const navigateToPreviousStep = () => {
    if (step === step2) {
      setStep(step1);
      setNoLegalBasis(false);
    }
  };

  const onSubmit: SubmitHandler<CreateDataRequestDto> = async (data) => {
    if (step === step2) {
      setIsSubmitting(true);
      try {
        await mutateAsync({ data });
      } catch (err) {
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (step === step1) {
        setStep(step2);
      }
    }
  };

  const Step = () => {
    const StepComponent = [DataServicesFormStep1, DataServicesFormStep2][step];
    return <StepComponent setNoLegalBasis={setNoLegalBasis} />;
  };

  return (
    <Page>
      <PageHeader
        title={tdr('heading')}
        backOnClick={
          step !== step1
            ? navigateToPreviousStep
            : () => navigate('/datadiensten')
        }
        previousText={
          step !== step1 ? t('common.steps.previous') : tdr('button_back')
        }
        details={
          <div className="-mx-6 mt-4 flex justify-center border-t border-border-minimal-enabled px-6 pt-3 ">
            <Stepper
              steps={[
                {
                  children: tdr('step-one-your-datarequest'),
                },
                {
                  children: tdr('step-two-submit-application'),
                },
              ].map((s, index) => ({
                ...s,
                active: index === step,
                done: index < step,
                onClick:
                  index < step
                    ? () => {
                        setStep(index);
                        if (index === step1) {
                          setNoLegalBasis(false);
                        }
                      }
                    : undefined,
              }))}
            />
          </div>
        }
      />
      <FormProvider {...formHelpers}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card size="md" className="mx-auto max-w-4xl">
            <Stack gap="md" divider="line">
              {Step()}
              <footer className="flex justify-between gap-x-4">
                <Button as={Link} to=".." action="tertiary" emphasis="subtle">
                  {t('common.button.cancel')}
                </Button>
                <Button
                  isLoading={isSubmitting}
                  action={step === step2 ? 'primary' : 'secondary'}
                  emphasis="bold"
                  type="submit"
                  disabled={noLegalBasis}
                >
                  {step === step2 ? t('common.submit') : t('common.steps.next')}
                </Button>
              </footer>
            </Stack>
          </Card>
          <CancelDataServicesRequest shouldBlock={shouldBlock} />
        </form>
      </FormProvider>
    </Page>
  );
};
